// An app's state in plain words (02-DESIGN/01-identity/04-apps.md § revocation
// and rotation, § re-attestation and suspension; decision 0033): connected,
// disconnected, or suspended — a personal app whose person has not signed in
// to the workspace within the install's window. The registration stays
// active while suspended; the registrant's next sign-in or an admin's restore
// lifts it.

import type { Tone } from "../../ui/components.tsx";
import { AppKind, type App } from "../../wire/auth.ts";

/** set reports whether an RFC 3339 time the wire carries is one, not absent or Go's zero time. */
export function set(t: string | undefined): t is string {
  return t !== undefined && t !== "" && !t.startsWith("0001");
}

/** isSuspended reports whether an app is a personal app the sweep suspended and nothing has restored. */
export function isSuspended(a: Pick<App, "active" | "kind" | "suspended">): boolean {
  return a.active && a.kind === AppKind.personal && set(a.suspended);
}

/** appState is an app's state as the page says it. */
export function appState(a: Pick<App, "active" | "kind" | "suspended">): { word: string; tone: Tone } {
  if (!a.active) {
    return { word: "disconnected", tone: "neutral" };
  }
  if (isSuspended(a)) {
    return { word: "suspended", tone: "bad" };
  }
  return { word: "connected", tone: "live" };
}

/** lastAttested is when a personal app was last confirmed: its person's sign-in or an admin's restore, or its connecting where none was recorded. */
export function lastAttested(a: Pick<App, "attested" | "created">): string {
  return set(a.attested) ? a.attested : a.created;
}

/**
 * lastUsed is when an app was last used — its latest exchange at the gateway
 * (04-apps.md § the exchange; hq decision 0072) — or undefined where it has
 * not been used yet.
 */
export function lastUsed(a: Pick<App, "used">): string | undefined {
  return set(a.used) ? a.used : undefined;
}

/** latestUse is the latest use among apps, or undefined where none has been used. */
export function latestUse(apps: readonly Pick<App, "used">[]): string | undefined {
  return apps
    .map(lastUsed)
    .filter((t): t is string => t !== undefined)
    .sort((a, b) => Date.parse(a) - Date.parse(b))
    .at(-1);
}

/** usedWords is an app's last use on its page: how long ago, or *never*. */
export function usedWords(
  a: Pick<App, "used">,
  ago: (t: string) => string,
  at: (t: string) => string,
): string {
  const t = lastUsed(a);
  return t === undefined ? "never" : ago(t) || at(t);
}

/**
 * suspendedWhy says why an app is suspended and what lifts it, to the person
 * reading it: its own registrant, or an admin, who may restore it.
 */
export function suspendedWhy(by: string, mine: boolean, workspace: string, restores: boolean): string {
  const who = mine ? "you have" : `${by} has`;
  const lifts = mine
    ? "It works again the next time you sign in"
    : `It works again the next time ${by} signs in`;
  return `Its requests are refused: ${who} not signed in to ${workspace} for longer than this install allows. ${lifts}${restores ? ", or when you restore it" : ""}.`;
}

/** RESTORE_CONSEQUENCE is what an admin's restore does and vouches for. */
export function restoreConsequence(by: string, workspace: string): string {
  return `It works again at once, for a fresh window. You vouch that ${by} still belongs to ${workspace}.`;
}

/** keyMade is when an app's current key was made: its last *New key*, or its connecting. */
export function keyMade(a: Pick<App, "rotated" | "created">): string {
  return set(a.rotated) ? a.rotated : a.created;
}

/** Fact is one line of an app's page: its label, its value, and a quiet note after it where there is one. */
export interface Fact {
  label: string;
  value: string;
  note?: string;
}

/**
 * appFacts are the lines of an app's page between its status and its id:
 * who connected it (to a role that lists every app) and when, its last use,
 * when an active personal app was last confirmed — a tenant app is never
 * attested (04-apps.md § re-attestation and suspension) — and its key's age.
 */
export function appFacts(
  app: Pick<App, "kind" | "active" | "registrant" | "created" | "rotated" | "attested" | "used">,
  o: { listsAny: boolean; ago: (t: string) => string; at: (t: string) => string },
): Fact[] {
  const either = (t: string) => o.ago(t) || o.at(t);
  const facts: Fact[] = [];
  if (o.listsAny) {
    facts.push({ label: "Connected by", value: app.registrant.display ?? app.registrant.subject });
  }
  facts.push({ label: "Connected", value: o.at(app.created), note: o.ago(app.created) });
  facts.push({ label: "Last used", value: usedWords(app, o.ago, o.at) });
  if (app.kind === AppKind.personal && app.active) {
    facts.push({ label: "Last confirmed", value: either(lastAttested(app)) });
  }
  facts.push({ label: "Its key", value: `made ${either(keyMade(app))}` });
  return facts;
}

/**
 * appsTileNote is the note under Home's Apps tile: when one was last used
 * (01-the-console.md § the surfaces: Home; hq decision 0072), and how many act
 * as a person to a role that lists every app.
 */
export function appsTileNote(
  connected: readonly Pick<App, "kind" | "used">[],
  listsAny: boolean,
  ago: (t: string) => string,
  at: (t: string) => string,
): string {
  if (connected.length === 0) {
    return "none yet";
  }
  const latest = latestUse(connected);
  const used = latest === undefined ? "none used yet" : `last used ${ago(latest) || at(latest)}`;
  if (!listsAny) {
    return used;
  }
  return `${String(connected.filter((a) => a.kind === AppKind.personal).length)} act as a person · ${used}`;
}
